import React, { useCallback } from "react";
import { useFormikContext, getIn } from "formik";

import { FieldGroup } from "../../components";
import { Visible } from "../../core/components";
import { FolderList } from "../../views/Listing/index";

// Source of truth for the images array is Formik. Previously this component
// kept a parallel local `useState([])` and FieldGroup synced it back into
// Formik via a useEffect — but on initial mount the local `[]` was written
// over the listing's existing image IDs (loaded by getInitialValues), so
// the gallery preview rendered empty even though the listing had images.
// Using Formik directly removes the dual-source-of-truth and makes folder
// additions / removals show up in the gallery immediately.
// Compare id lists tolerant of number/string mismatches — folder ids and the
// ids RUG derives via parseInt can differ in type while being the same image,
// and that must NOT register as a user edit.
const sameIds = (a = [], b = []) =>
  a.length === b.length && a.every((v, i) => String(v) === String(b[i]));

// Stable reference for the "no images" case. Using an inline `|| []` here minted
// a NEW array every render whenever the read field was empty/non-array (e.g. the
// create flow seeds `images` to undefined). That new-each-render value flowed
// into GalleryUpload's `useEffect(..., [value])`, which calls setFieldValue on
// every change → re-render → new array → infinite loop ("Maximum update depth
// exceeded"). It froze and crashed any marketplace tab whose gallery mounts
// before DAO images exist (CH24 UK et al). Keep this reference constant.
const EMPTY_IMAGES = [];

const ProductImages = ({
  readOnly = false,
  nameObject,
  showTitle = true,
  productId = null,
  // Formik path to READ the displayed images from. An untouched marketplace
  // tab reads DAO ("images") so it mirrors DAO live; once forked it reads its
  // own scope. Defaults preserve legacy (DAO listing / product images).
  readFieldName = null,
  // Formik path to WRITE to on edit. For a marketplace tab this is its own
  // scope ("marketplace_images.ebay"), so editing an UNTOUCHED tab forks a
  // copy of DAO into the scope instead of mutating DAO itself.
  writeFieldName = null,
  // Fired when the USER actually changes the set (upload / remove / reorder /
  // folder add) — i.e. the array changed. Marks a marketplace tab "touched"
  // so it stops following DAO. NOT fired for mount / no-op writes.
  onUserEdit = null,
  // Ordered list of position labels (e.g. WatchFinder slots). When provided,
  // each thumbnail is badged with the label for its position so reps order
  // images to match the marketplace feed. null = no labels (default).
  slotLabels = null,
}) => {
  const formik = useFormikContext();
  const legacyName = nameObject ? "product.images" : "images";
  const readName = readFieldName ?? legacyName;
  const writeName = writeFieldName ?? legacyName;
  // FieldGroup name drives the bound Formik <Field> + RUG's internal writes;
  // point it at the WRITE target so user edits land on the right path.
  const fieldGroupName = nameObject ? "product[images]" : writeName;

  // Display value is read from readName (DAO for an untouched tab). Coerce a
  // missing/non-array value to a STABLE empty array (EMPTY_IMAGES) — never an
  // inline `[]` literal, which would change identity every render and drive
  // GalleryUpload's value-keyed effect into an infinite setFieldValue loop.
  const rawImages = formik ? getIn(formik.values, readName) : undefined;
  const images = Array.isArray(rawImages) ? rawImages : EMPTY_IMAGES;

  // Read formik.values at call time (not the captured `images`) so rapid
  // successive setImages calls — e.g. checking two folders in quick
  // succession — both see the latest committed value and don't trample
  // each other. Base the functional update on what's DISPLAYED (readName),
  // write the result to writeName.
  const setImages = useCallback(
    (next) => {
      if (!formik) return;
      const current = getIn(formik.values, readName) || [];
      const value = typeof next === "function" ? next(current) : next;
      formik.setFieldValue(writeName, value);
      // Only a genuine change counts as a user edit; RUG fires onChange on
      // mount with identical ids, which must NOT mark the tab touched.
      if (onUserEdit && !sameIds(current, value)) {
        onUserEdit();
      }
    },
    [formik, readName, writeName, onUserEdit]
  );

  return (
    <div className="row">
      <Visible when={showTitle}>
        <div className="col-md-3">
          <h3>Upload Photos</h3>
        </div>
      </Visible>
      <div className={`col-md-${showTitle ? 9 : 12}`}>
        <FieldGroup
          className={"mt-0"}
          placeholder=""
          name={fieldGroupName}
          type="galleryUpload"
          readOnly={readOnly}
          value={images}
          onChange={(e) => setImages(e)}
          // Mirroring (read ≠ write) = an untouched tab showing DAO; key RUG on
          // order so it reflects DAO's order live. Self-editing (read = write)
          // keeps a membership key so a drag isn't interrupted by a remount.
          reflectOrder={readName !== writeName}
          slotLabels={slotLabels}
        />
      </div>
      <div className="col-md-12">
        <FolderList setImages={setImages} productId={productId} />
      </div>
    </div>
  );
};

export default ProductImages;
